// The file repository's folder rail.
//
// A SIBLING of `AssemblyTree.js` rather than a reuse of it: that component is
// bound to `CADViewer`'s STEP assembly shape and, more to the point, is a
// read-only selector — no drop target, no rename, no delete, no "New folder".
// Its conventions are copied deliberately (recursive `TreeNode`, the
// `8 + depth * 16` indent, the `▾/▸` caret) so the two rails read alike.
//
// Three things this adds:
//
//   * an "All files" root row, which is a real destination — dropping a file on
//     it moves that file back to the repository root;
//   * drop targets on every row, since dragging a selection onto a folder is the
//     primary way files get organized; and
//   * per-row rename/delete, using the app's canonical inline-edit interaction
//     (Enter commits / Escape cancels / blur commits) rather than a modal.
//
// A node with `folderId: null` is a SYNTHESIZED intermediate — `fileOrganize`
// invents one when a folder exists at `A/B` with no row for `A`, which the
// backend's reparenting delete can legitimately produce. It renders as an
// ordinary folder but cannot be renamed or deleted, because there is no record
// behind it to address.

import { useEffect, useRef, useState } from 'react';
import './RepoFolderTree.css';

function TreeNode({
  node,
  selectedPath,
  onSelect,
  onDropFiles,
  onRename,
  onDelete,
  expanded,
  onToggle,
  depth,
  readOnly,
}) {
  const hasChildren = node.children && node.children.length > 0;
  const open = expanded.has(node.path);
  const isSelected = node.path === selectedPath;
  const [dragOver, setDragOver] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(node.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (renaming) inputRef.current?.select();
  }, [renaming]);

  function handleToggle(e) {
    e.stopPropagation();
    onToggle(node.path);
  }

  function commitRename() {
    const next = draft.trim();
    setRenaming(false);
    // Unchanged or emptied is a cancel, not a request to rename to nothing.
    if (!next || next === node.name) return;
    onRename(node, next);
  }

  function startRename(e) {
    e.stopPropagation();
    setDraft(node.name);
    setRenaming(true);
  }

  return (
    <div className="rft-node">
      <div
        className={
          'rft-row' +
          (isSelected ? ' rft-row--selected' : '') +
          (dragOver ? ' rft-row--dragover' : '')
        }
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => onSelect(node.path)}
        onDragOver={(e) => {
          if (readOnly) return;
          // Both are required for a drop to fire at all.
          e.preventDefault();
          e.stopPropagation();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (readOnly) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          onDropFiles(node.path, e);
        }}
      >
        <span
          className={`rft-toggle${hasChildren ? '' : ' rft-toggle--leaf'}`}
          onClick={hasChildren ? handleToggle : undefined}
        >
          {hasChildren ? (open ? '▾' : '▸') : '·'}
        </span>
        <span className="rft-icon">{open && hasChildren ? '📂' : '📁'}</span>
        {renaming ? (
          <input
            ref={inputRef}
            className="rft-rename-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setRenaming(false);
            }}
            onBlur={commitRename}
          />
        ) : (
          <span className="rft-name" title={node.path}>
            {node.name}
          </span>
        )}
        {!renaming && node.fileCount > 0 && <span className="rft-count">{node.fileCount}</span>}
        {!renaming && !readOnly && node.folderId && (
          <span className="rft-actions">
            <button
              type="button"
              className="rft-action"
              title="Rename folder"
              onClick={startRename}
            >
              ✏
            </button>
            <button
              type="button"
              className="rft-action rft-action--delete"
              title="Delete folder (files move up one level)"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(node);
              }}
            >
              🗑
            </button>
          </span>
        )}
      </div>
      {hasChildren && open && (
        <div className="rft-children">
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              selectedPath={selectedPath}
              onSelect={onSelect}
              onDropFiles={onDropFiles}
              onRename={onRename}
              onDelete={onDelete}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function RepoFolderTree({
  tree,
  selectedPath,
  totalCount = 0,
  onSelect,
  onDropFiles,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  readOnly = false,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [dragOverRoot, setDragOverRoot] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const createRef = useRef(null);

  useEffect(() => {
    if (creating) createRef.current?.focus();
  }, [creating]);

  // Reveal the selected folder's ancestors, so selecting `A/B` from a breadcrumb
  // or a search result does not leave it hidden behind a collapsed `A`. Additive:
  // it never collapses what the user opened by hand.
  useEffect(() => {
    if (!selectedPath) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      const segments = selectedPath.split('/');
      let acc = '';
      let changed = false;
      for (const seg of segments.slice(0, -1)) {
        acc = acc ? `${acc}/${seg}` : seg;
        if (!next.has(acc)) {
          next.add(acc);
          changed = true;
        }
      }
      // Identity-stable when nothing was revealed, or this loops on its own dep.
      return changed ? next : prev;
    });
  }, [selectedPath]);

  function toggle(path) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  function commitCreate() {
    const name = draft.trim();
    setCreating(false);
    setDraft('');
    if (!name) return;
    // Created inside whatever is selected, which is what "New folder" means
    // when a folder is open.
    onCreateFolder(selectedPath || '', name);
  }

  const isRoot = selectedPath === '';
  const nodes = tree || [];

  return (
    <div className="rft-root">
      <div className="rft-header">
        <span className="rft-header-label">Folders</span>
        {!readOnly && (
          <button
            type="button"
            className="rft-new"
            title="New folder"
            onClick={() => setCreating(true)}
          >
            + New
          </button>
        )}
      </div>

      <div className="rft-scroll">
        {/* "All files" is a real destination: dropping here moves a file to the root. */}
        <div
          className={
            'rft-row rft-row--all' +
            (isRoot ? ' rft-row--selected' : '') +
            (dragOverRoot ? ' rft-row--dragover' : '')
          }
          onClick={() => onSelect('')}
          onDragOver={(e) => {
            if (readOnly) return;
            e.preventDefault();
            setDragOverRoot(true);
          }}
          onDragLeave={() => setDragOverRoot(false)}
          onDrop={(e) => {
            if (readOnly) return;
            e.preventDefault();
            setDragOverRoot(false);
            onDropFiles('', e);
          }}
        >
          <span className="rft-toggle rft-toggle--leaf">·</span>
          <span className="rft-icon">🗃</span>
          <span className="rft-name">All files</span>
          {totalCount > 0 && <span className="rft-count">{totalCount}</span>}
        </div>

        {nodes.map((node) => (
          <TreeNode
            key={node.path}
            node={node}
            selectedPath={selectedPath}
            onSelect={onSelect}
            onDropFiles={onDropFiles}
            onRename={onRenameFolder}
            onDelete={onDeleteFolder}
            expanded={expanded}
            onToggle={toggle}
            depth={0}
            readOnly={readOnly}
          />
        ))}

        {creating && (
          <div className="rft-row rft-row--creating" style={{ paddingLeft: '8px' }}>
            <span className="rft-toggle rft-toggle--leaf">·</span>
            <span className="rft-icon">📁</span>
            <input
              ref={createRef}
              className="rft-rename-input"
              placeholder="Folder name"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitCreate();
                if (e.key === 'Escape') {
                  setCreating(false);
                  setDraft('');
                }
              }}
              onBlur={commitCreate}
            />
          </div>
        )}

        {!nodes.length && !creating && (
          <p className="rft-empty">
            {readOnly ? 'No folders.' : 'No folders yet. Create one to start organizing.'}
          </p>
        )}
      </div>

      {/* Both stated rather than left to be discovered: a modifier nobody knows
          about is not a feature, and "delete" beside a folder full of files
          invites the worst assumption. */}
      {!readOnly && nodes.length > 0 && (
        <p className="rft-hint">
          Drag files onto a folder to move them; hold <kbd>Ctrl</kbd> to copy. Deleting a folder
          moves its contents up one level.
        </p>
      )}
    </div>
  );
}
